import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConsistentHeader } from 'containers/ConsistentHeader/ConsistentHeader'
import { fetchReqTokenAndUser, fetchUser, getUserLoggedInState } from 'redux/reducers/users'
import SimpleFooter from 'components/Footer/SimpleFooter'
import { SkipNav } from '@cmsgov/design-system'
import { useTranslation } from 'react-i18next'
import { getModalType } from 'redux/reducers/modal'
import { useLocation } from 'react-router-dom'

import { SEO } from 'containers/SEO/SEO'
import { seoMetaData } from 'data/seo'
import { getLanguageToggleUrl } from 'utils/language'
import { UrlCleaner } from 'components/UrlCleaner/UrlCleaner'
import { AriaLog } from 'components/AriaLog/AriaLog'
import { AppRoutes } from 'components/AppRoutes/AppRoutes'

import useLanguage from 'hooks/useLanguage'
import useStopBackgroundScroll from 'hooks/useStopBackgroundScroll'
import useScrollAndFocusOnPathChange from 'hooks/useScrollAndFocusOnPathChange'
import { useClearSPACookiesOnLogout } from 'hooks/useClearSPACookiesOnLogout'

import ErrorBoundary from 'errors/ErrorBoundary'
import { RelayRedirect } from 'components/RelayRedirect/RelayRedirect'
import { useLDSafeFlags } from 'hooks/useLDSafeFlags'
import { MySpinner } from 'components/MySpinner/MySpinner'

export const mainHeadingId = 'maincontent'
const isProduction = process.env.REACT_APP_MY_MEDICARE_ENV === 'www'

interface AppProps {}

export const App: React.FC<AppProps> = () => {
  // don't allow iframes to take priority when they exist in the document body element
  document.body.classList.add('setIframeZIndex')

  useClearSPACookiesOnLogout()
  useLanguage()
  useScrollAndFocusOnPathChange()

  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()

  const modalTypeOpen = useSelector(getModalType)
  useStopBackgroundScroll(modalTypeOpen)

  const userLoggedIn = useSelector(getUserLoggedInState)
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false)
  const handleSkipNavClick = (event: Event) => {
    event.preventDefault()
    document.getElementById(mainHeadingId)?.focus()
  }

  const location = useLocation()
  const getLanguageToggleLinkCallback = useCallback(getLanguageToggleUrl, [
    i18n.language,
    location,
    isProduction
  ])

  // Used to wait until LD is loaded before fetching user and rendering page
  const flags = useLDSafeFlags()
  const userFetched = useRef(false)
  const ldLoaded = (
    flags !== undefined &&
    (
      flags === 'error' ||
      (
        typeof flags === 'object' &&
        flags.useOnlySlsJwtForAuthByDefault !== undefined
      )
    )
  )
  useEffect(() => {
    // TODO: Remove once flags?.useOnlySlsJwtForAuthByDefault is no longer needed
    // Launch Darkly temporarily returns an empty object after it finishes, so we need
    // to have extra logic here to make sure it's either 'error' or that it's an object
    // with the useOnlySlsJwtForAuthByDefault flag populated
    if (ldLoaded && !userFetched.current) {
      if (
        flags === 'error' || !flags?.useOnlySlsJwtForAuthByDefault
      ) {
        dispatch(fetchReqTokenAndUser())
      } else {
        dispatch(fetchUser())
      }
      userFetched.current = true
    }
  }, [dispatch, ldLoaded, flags])

  return (
    <ErrorBoundary>
      {ldLoaded && (
        <RelayRedirect>
          <UrlCleaner />
          <SkipNav href={`#${mainHeadingId}`} onClick={handleSkipNavClick}>
            {t('common:skipnav')}
          </SkipNav>
          <SEO seoMetaData={seoMetaData} />
          <ConsistentHeader
            languageToggleLink={getLanguageToggleLinkCallback(
              i18n.language,
              window.location.href,
              isProduction
            )}
            showTimeoutMessage={() => setShowTimeoutMessage(true)}
            enableMessageCenter={Boolean(userLoggedIn)}
          />
          <AriaLog />
          <AppRoutes
            showTimeoutMessage={showTimeoutMessage}
            setShowTimeoutMessage={setShowTimeoutMessage}
          />
          <SimpleFooter />
        </RelayRedirect>
      )}
      {!ldLoaded && (
        <MySpinner />
      )}
    </ErrorBoundary>
  )
}
